import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Divider } from 'primereact/divider';
import { Skeleton } from 'primereact/skeleton';
import { Message } from 'primereact/message';
import { Toast } from 'primereact/toast';
import { verificationService } from '../services/verificationService';
import CustomPDFViewer from './CustomPDFViewer';
import logo30Years from '../assets/logo30Years.svg';
import BrandLine_Blue from '../assets/BrandLine_Blue.png';

const VerificationPage = () => {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');
  const [documentViewerUrl, setDocumentViewerUrl] = useState(null);
  const [isLoadingViewer, setIsLoadingViewer] = useState(false);
  const [viewerFailed, setViewerFailed] = useState(false);

  const MAX_VIEWER_ATTEMPTS = 1;
  const viewerAttempts = useRef(0);

  const toast = useRef(null);

  const { data: verificationData, isLoading, error } = useQuery({
    queryKey: ['verification', code],
    queryFn: () => verificationService.verifyDocument(code),
    enabled: !!code,
    retry: false,
  });

  const verificationResult = verificationData?.verificationResult;
  const document = verificationData?.document;

  const getStatusSeverity = (status) => {
    switch (status) {
      case 'VALID':
        return 'success';
      case 'INVALID':
        return 'danger';
      case 'REVOKED':
        return 'warning';
      case 'ARCHIVED':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const getStatusMessage = (status) => {
    switch (status) {
      case 'VALID':
        return 'Document with provided verification Code exists, kinldy verify your document against it';
      case 'INVALID':
        return 'This document is not valid or has been tampered with.';
      case 'REVOKED':
        return 'This document has been revoked by LHRC.';
      case 'ARCHIVED':
        return 'This document has been archived and is no longer active.';
      case 'NOT_FOUND':
        return 'Document not found. Please check the verification code.';
      default:
        return 'Unable to verify document.';
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const downloadFile = async (type) => {
    if (!code || typeof window === 'undefined') return;
    try {
      let blob;
      if (type === 'original') {
        blob = await verificationService.downloadOriginal(code);
      } else {
        blob = await verificationService.downloadQRProtected(code);
      }
      if (!blob) throw new Error('No file data received');

      const url = window.URL.createObjectURL(blob);
      const fileName =
        type === 'original'
          ? verificationData?.document?.title || 'document'
          : `qr_${verificationData?.document?.title || 'document'}`;

      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, fileName);
      } else {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      alert(`Download failed: ${error.message}`);
    }
  };

  const loadDocumentViewer = useCallback(async () => {
    if (!code) return;

    // prevent infinite hammering
    if (viewerAttempts.current >= MAX_VIEWER_ATTEMPTS) {
      setViewerFailed(true);
      return;
    }

    viewerAttempts.current++;
    setIsLoadingViewer(true);

    try {
      const blob = await verificationService.downloadQRProtected(code);

      if (!blob) {
        throw new Error('No blob returned');
      }

      const url = window.URL.createObjectURL(blob);

      setDocumentViewerUrl((previousUrl) => {
        if (previousUrl) {
          window.URL.revokeObjectURL(previousUrl);
        }
        return url;
      });

      setViewerFailed(false);
    } catch (error) {
      console.error('Viewer load error:', error);
      if (toast.current) {
        toast.current.show({
          severity: 'error',
          summary: 'Viewer Failed',
          detail: 'Failed to load document for viewing.',
        });
      }
    } finally {
      setIsLoadingViewer(false);
    }
  }, [code]);

  useEffect(() => {
    return () => {
      if (documentViewerUrl) {
        window.URL.revokeObjectURL(documentViewerUrl);
      }
    };
  }, [documentViewerUrl]);

  useEffect(() => {
    if (
      verificationResult === 'VALID' &&
      !documentViewerUrl &&
      !isLoadingViewer &&
      !viewerFailed
    ) {
      loadDocumentViewer();
    }
  }, [
    verificationResult,
    documentViewerUrl,
    isLoadingViewer,
    viewerFailed,
    loadDocumentViewer,
  ]);

  if (!code) {
    return (
      <div className="verification-container">
        <Card>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-primary mb-3">
              Document Verification
            </h1>
            <Message
              severity="warn"
              text="Please provide a verification code to check document authenticity."
            />
            <p className="text-muted mt-3">
              Enter a verification code in the URL: /verify?code=XXXXXX
            </p>
          </div>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="verification-container">
        <Card>
          <div className="text-center mb-4">
            <Skeleton width="300px" height="2rem" className="mb-3" />
            <Skeleton width="200px" height="1.5rem" />
          </div>
          <div className="grid">
            <div className="col-12 md:col-6">
              <Skeleton width="100%" height="200px" />
            </div>
            <div className="col-12 md:col-6">
              <Skeleton width="100%" height="200px" />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  if (error || !verificationData) {
    return (
      <div className="verification-container">
        <Card>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-primary mb-3">
              Document Verification
            </h1>
            <Message
              severity="error"
              text={error?.message || 'Failed to verify document'}
            />
            <p className="text-muted mt-3">
              Verification Code: <strong>{code}</strong>
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="verification-container">
      <Toast ref={toast} />
      <Card className="verification-card">
        {/* Header */}
        <div className="verification-header">
          <div className="flex align-items-center justify-content-between px-4">
            <div>
              <img
                src={logo30Years}
                alt="Document Logo"
                style={{ width: '150px', height: '150px' }}
              />
            </div>

            <div>
              <h1 className="text-3xl font-bold text-primary mb-2">
                LHRC Documents Verification Platform
              </h1>
              <p className="text-muted">
                Avoid distribution of fake documents by verifying them through
                this platform.
              </p>
            </div>
            <div></div>
          </div>
        </div>
        <Divider align="left" className="section-divider mb-6"></Divider>

        {/* Document Viewer */}
        {verificationResult === 'VALID' && (
          <Card className="mb-4 mx-4 p-4">
            <div className="flex align-items-center justify-content-between flex-wrap gap-3">
              <h3 className="m-0">Protected Document Viewer</h3>
              <div className="flex align-items-center gap-2">
                <Button
                  label="Reload"
                  icon="pi pi-refresh"
                  className="p-button-outlined p-button-sm"
                  onClick={() => {
                    viewerAttempts.current = 0;
                    setViewerFailed(false);
                    loadDocumentViewer();
                  }}
                  disabled={isLoadingViewer}
                  outlined
                />
                {documentViewerUrl && (
                  <Button
                    label="Download"
                    icon="pi pi-download"
                    className="p-button-outlined p-button-sm"
                    onClick={() => window.open(documentViewerUrl, '_blank')}
                  />
                )}
              </div>
            </div>
            <Divider />

            {isLoadingViewer ? (
              <div className="text-center p-4">
                <Skeleton width="100%" height="420px" />
                <p className="text-sm text-muted mt-2">Loading document...</p>
              </div>
            ) : documentViewerUrl ? (
              <div className="surface-border border-round overflow-hidden">
                {document.mimeType.includes('pdf') ? (
                  <CustomPDFViewer blobUrl={documentViewerUrl} />
                ) : document.mimeType.includes('image') ? (
                  <img
                    src={documentViewerUrl}
                    alt="Document Preview"
                    style={{
                      width: '100%',
                      height: '520px',
                      objectFit: 'contain',
                      backgroundColor: '#f8f9fa',
                    }}
                  />
                ) : (
                  <div className="text-center p-4">
                    <i className="pi pi-file text-4xl text-muted mb-2"></i>
                    <p className="text-muted mb-3">
                      Preview not available for this file type.
                    </p>
                    <Button
                      label="Download QR-Protected"
                      icon="pi pi-download"
                      className="p-button-outlined p-button-sm"
                      onClick={() => downloadFile('qr-protected')}
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center p-4 border-1 surface-border border-round">
                <i className="pi pi-exclamation-triangle text-2xl text-muted mb-2"></i>
                <p className="text-sm text-muted mb-2">
                  Document preview unavailable.
                </p>
                <Button
                  label="Try Again"
                  icon="pi pi-refresh"
                  className="p-button-text p-button-sm"
                  onClick={() => {
                    viewerAttempts.current = 0;
                    setViewerFailed(false);
                    loadDocumentViewer();
                  }}
                  disabled={isLoadingViewer}
                />
              </div>
            )}
          </Card>
        )}

        {/* Verification Status */}
        <div
          className={`verification-status mx-4 p-4 ${verificationResult.toLowerCase()}`}
        >
          <div className="flex align-items-center justify-content-center gap-3 mb-2">
            <i
              className={`pi ${
                verificationResult === 'VALID'
                  ? 'pi-check-circle'
                  : verificationResult === 'INVALID'
                  ? 'pi-times-circle'
                  : verificationResult === 'REVOKED'
                  ? 'pi-exclamation-triangle'
                  : 'pi-question-circle'
              } text-2xl`}
            ></i>
            <h2 className="m-0 text-xl font-bold">
              {verificationResult === 'VALID'
                ? 'DOCUMENT EXISTS'
                : verificationResult === 'INVALID'
                ? 'INVALID'
                : verificationResult === 'REVOKED'
                ? 'REVOKED'
                : verificationResult === 'ARCHIVED'
                ? 'ARCHIVED'
                : 'NOT FOUND'}
            </h2>
          </div>
          <p className="m-0 text-center">
            {getStatusMessage(verificationResult)}
          </p>
        </div>

        {/* Document Information */}
        {document && (
          <div className="grid ">
            <div className="col-12 md:col-8">
              <Card className="mx-4 p-4 pb-0">
                <h3 className="mt-0 mb-3">Document Information</h3>

                <div className="grid">
                  <div className="col-12 md:col-6">
                    <div className="field">
                      <label className="font-medium text-muted">Title</label>
                      <p
                        className="m-0 mt-1 text-wrap break-word overflow-hidden"
                        style={{
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                        }}
                      >
                        {document.title}
                      </p>
                    </div>
                  </div>
                  <div className="col-12 md:col-6">
                    <div className="field">
                      <label className="font-medium text-muted">
                        Verification Code
                      </label>
                      <p className="m-0 mt-1 font-mono font-bold">
                        {document.shortCode}
                      </p>
                    </div>
                  </div>
                  <div className="col-12 md:col-6">
                    <div className="field">
                      <label className="font-medium text-muted">Status</label>
                      <div className="mt-1">
                        <Tag
                          value={document.status}
                          severity={getStatusSeverity(document.status)}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="col-12 md:col-6">
                    <div className="field">
                      <label className="font-medium text-muted">
                        File Size
                      </label>
                      <p className="m-0 mt-1 font-bold">
                        {formatFileSize(document.fileSize)}
                      </p>
                    </div>
                  </div>

                  <div className="col-12 md:col-6"></div>

                  <div className="col-12 md:col-6">
                    <div className="field">
                      <label className="font-medium text-muted">
                        Upload Date
                      </label>
                      <p className="m-0 mt-1 font-bold">
                        {formatDate(document.createdAt)}
                      </p>
                    </div>
                  </div>

                  {document.description && (
                    <div className="col-12">
                      <div className="field">
                        <label className="font-medium text-muted">
                          Description
                        </label>
                        <p className="m-0 mt-1">{document.description}</p>
                      </div>
                    </div>
                  )}

                  {document.tags &&
                    Array.isArray(document.tags) &&
                    document.tags.length > 0 && (
                      <div className="col-12">
                        <div className="field">
                          <label className="font-medium text-muted">
                            Tags
                          </label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {document.tags.map((tag, index) => (
                              <Tag key={index} value={tag} severity="info" />
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                </div>

                <Divider />
              </Card>
            </div>

            <div className="col-12 md:col-4">
              <Card className="mx-4 p-4">
                <h3 className="mt-0 mb-3">Verification Tools</h3>

                {verificationResult === 'VALID' && (
                  <div className="flex flex-column gap-2 m-4 mx-6">
                    <Button
                      label="Download QR-Protected"
                      icon="pi pi-qrcode"
                      className="p-button-outlined"
                      onClick={() => downloadFile('qr-protected')}
                    />
                  </div>
                )}

                <Divider />

                <div className="text-center">
                  <h4 className="mt-0 mb-2">Verification URL</h4>
                  <p className="text-sm text-muted break-all">
                    {verificationData.verificationUrl}
                  </p>
                  <Button
                    label="Copy URL"
                    icon="pi pi-copy"
                    className="p-button-text p-button-sm mt-2"
                    onClick={() => {
                      navigator.clipboard.writeText(
                        verificationData.verificationUrl
                      );
                      toast.current.show({
                        severity: 'success',
                        summary: 'Copied to clipboard',
                        detail: 'Verification URL copied to clipboard',
                      });
                    }}
                  />
                </div>
              </Card>
            </div>
          </div>
        )}

        <Card className="mx-4 mb-8 p-4">
          <h4 className="mt-0 mb-3">
            {' '}
            <i className="pi pi-shield mr-2 font-bold"></i> Security Information
          </h4>
          <div className="field">
            <label className="font-medium text-muted">SHA-256 Hash</label>
            <p className="m-0 mt-1 font-mono text-lg font-bold break-all pb-4">
              {document.sha256Hash}
            </p>
            <small className="text-muted ">
              This hash can be used to verify the document's integrity
            </small>
          </div>
        </Card>

        <Divider />
        <div className="text-center">
          <div className="flex align-items-center justify-content-center ">
            <img
              src={BrandLine_Blue}
              alt="Document Logo"
              style={{ width: '100%', height: '50px' }}
            />
          </div>

          <div className="text-sm text-muted p-4">
            ©{' '}
            <span className="font-bold">
              {new Date().getFullYear()}
            </span>{' '}
            Legal and Human Rights Centre. All rights reserved.
          </div>
        </div>
      </Card>
    </div>
  );
};

export default VerificationPage;
